// src/components/layout/Sidebar/Sidebar.jsx
// -----------------------------------------------------------------------------
// AFix left navigation, expanded to cover every page in the project:
//   Dashboard
//   Notifications
//   Tickets        -> All Tickets (TicketList), New Ticket (NewTicketForm)
//   Projects       -> All Projects (ProjectList)
//   TicketTypes    -> All Ticket Types (TicketTypeList)
//   Users
//   Roles
//   ClientOrganizations
//
// Detail pages (TicketDetail, ProjectDetail) are dynamic routes reached by
// clicking a row in their list page (e.g. /tickets/:id), so they intentionally
// don't get their own sidebar link. Auth (Login/Register) is pre-login and
// isn't part of the authenticated nav, so it's left out too — say the word if
// you want it in here anyway.
// -----------------------------------------------------------------------------
import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, Link, useLocation } from 'react-router-dom';
import afixLogo from '../../assets/images/afix-logo.png';
import { useAuth, ROLES } from '../../context/AuthContext';
import './Sidebar.css';
import {
  DashboardIcon,
  TicketIcon,
  FolderIcon,
  UserIcon,
  ShieldIcon,
  BuildingIcon,
  BellIcon,
  ChevronDownIcon,
} from '../common/Icons';

// Which roles may see each menu path - keep in sync with allowedRoles in App.jsx
// and the [Authorize(Roles = ...)] rules on the API. Paths not listed are
// visible to every logged-in user.
const ADMIN = [ROLES.ADMIN];
const ADMIN_PM = [ROLES.ADMIN, ROLES.PROJECT_MANAGER];
const INTERNAL = [ROLES.ADMIN, ROLES.PROJECT_MANAGER, ROLES.TEAM_MEMBER];
const ROLES_BY_PATH = {
  '/users': ADMIN,
  '/roles': ADMIN,
  '/audit-log': ADMIN,
  '/reports': INTERNAL,
  '/ticket-types': ADMIN,
  '/ticket-link-types': ADMIN,
  '/ticket-mandatory-fields': ADMIN_PM,
  '/ticket-reopen-reasons': ADMIN_PM,
  '/ticket-severities': ADMIN_PM,
  '/ticket-statuses': ADMIN_PM,
  '/ticket-status-transitions': ADMIN_PM,
  '/project-phases': ADMIN_PM,
  '/project-environments': ADMIN_PM,
  '/project-teams': ADMIN_PM,
  '/project-team-members': ADMIN_PM,
  '/client-organizations': ADMIN_PM,
  '/client-questions': ADMIN_PM,
  '/projects': INTERNAL,
  '/ticket-status-history': INTERNAL,
};

// Adjust these paths to match your actual route definitions if different.
const NAV_SECTIONS = [
  {
    label: 'MAIN',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: <DashboardIcon size={16} />, to: '/dashboard' },
      { key: 'reports', label: 'Reports', icon: <DashboardIcon size={16} />, to: '/reports' },
      {
        key: 'tickets',
        label: 'Tickets',
        icon: <TicketIcon size={16} />,
        to: '/tickets',
        children: [
          { key: 'ticket', label: 'Ticket', to: '/tickets' },
          { key: 'ticket-status-history', label: ' Ticket Status History', to: '/ticket-status-history' },
          { key: 'ticket-link-types', label: 'Ticket Link Types', to: '/ticket-link-types' },
          { key: 'ticket-mandatory-fields', label: 'Mandatory Fields', to: '/ticket-mandatory-fields' },
          { key: 'all-ticket-types', label: 'Ticket Types', to: '/ticket-types' },
          { key: 'ticket-reopen-reasons', label: 'Reopen Reasons', to: '/ticket-reopen-reasons' },
          { key: 'ticket-severities', label: 'Severities', to: '/ticket-severities' },
          { key: 'ticket-statuses', label: 'Statuses', to: '/ticket-statuses' },
          { key: 'ticket-status-transitions', label: 'Status Transitions', to: '/ticket-status-transitions' },
        ],
      },

    ],
  },
  {
    label: 'PROJECT MANAGEMENT',
    items: [
      {
        key: 'projects',
        label: 'Projects',
        icon: <FolderIcon size={16} />,
        to: '/projects',
        children: [
          { key: 'all-projects', label: 'Projects', to: '/projects' },
          { key: 'project-phases', label: 'Project Phases', to: '/project-phases' },
          { key: 'project-environments', label: 'Project Environments', to: '/project-environments' },
          { key: 'project-teams', label: 'Project Teams', to: '/project-teams' },
          { key: 'project-team-members', label: 'Project Team Members', to: '/project-team-members' },
        ],
      },
    ],
  },
  {
    label: 'ADMINISTRATION',
    items: [
      { key: 'users', label: 'Users', icon: <UserIcon size={16} />, to: '/users' },
      { key: 'roles', label: 'Roles', icon: <ShieldIcon size={16} />, to: '/roles' },
      { key: 'audit-log', label: 'Audit Log', icon: <ShieldIcon size={16} />, to: '/audit-log' },
      // { key: 'permissions', label: 'Permissions', icon: '🔑', to: '/permissions' },
      {
        key: 'client-orgs-group',
        label: 'Client Organizations',
        icon: <BuildingIcon size={16} />,
        to: '/client-organizations',
        children: [
          { key: 'client-orgs', label: 'Client Organizations', to: '/client-organizations' },
          { key: 'client-questions', label: 'Client Questions', to: '/client-questions' },
        ],
      },
      { key: 'notifications', label: 'Notifications', icon: <BellIcon size={16} />, to: '/notifications' },
    ],
  },
];

export default function Sidebar({ collapsed, mobileOpen, onClose }) {
  const location = useLocation();
  const { hasRole } = useAuth();

  // Hide menu entries the logged-in user's role cannot open. A group is kept
  // only if at least one of its children is visible.
  const canSee = (to) => !ROLES_BY_PATH[to] || hasRole(...ROLES_BY_PATH[to]);
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .map((item) =>
        item.children ? { ...item, children: item.children.filter((c) => canSee(c.to)) } : item
      )
      .filter((item) => (item.children ? item.children.length > 0 : canSee(item.to))),
  })).filter((section) => section.items.length > 0);

  // Auto-expand a group if the current route matches one of its children.
  const initialOpen = visibleSections.flatMap((s) => s.items)
    .filter((item) => item.children?.some((c) => location.pathname.startsWith(c.to)))
    .map((item) => item.key)
    .slice(0, 1);

  const [openGroups, setOpenGroups] = useState(initialOpen);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => (prev.includes(key) ? [] : [key]));
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-logo">
        <Link to="/dashboard" style={{ display: 'flex', width: '100%', textDecoration: 'none', cursor: 'pointer', justifyContent: 'center' }}>
          {collapsed ? (
            <div className="sidebar-logo-card collapsed-logo-card">
              <img src={afixLogo} alt="AFix Logo" className="sidebar-logo-icon-img" />
            </div>
          ) : (
            <div className="sidebar-logo-card expanded-logo-card">
              <img src={afixLogo} alt="AFix Logo" className="sidebar-logo-img" />
            </div>
          )}
        </Link>
        {mobileOpen && (
          <button className="sidebar-close-btn" onClick={onClose} aria-label="Close sidebar">
            ✕
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        {visibleSections.map((section) => (
          <div className="sidebar-section" key={section.label}>
            <div className="sidebar-section-label">{section.label}</div>

            {section.items.map((item) =>
              item.children ? (
                <div className="sidebar-group" key={item.key}>
                  <div
                    className={`sidebar-item sidebar-item-parent ${location.pathname.startsWith(item.to) ? 'active' : ''
                      }`}
                    onClick={() => toggleGroup(item.key)}
                  >
                    <span className="sidebar-item-icon">{item.icon}</span>
                    <span className="sidebar-item-label">{item.label}</span>
                    <ChevronDownIcon
                      className={`sidebar-caret ${openGroups.includes(item.key) ? 'open' : ''}`}
                      size={12}
                    />
                  </div>

                  {openGroups.includes(item.key) && (
                    <div className="sidebar-submenu">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.key}
                          to={child.to}
                          end
                          className={({ isActive }) =>
                            `sidebar-subitem ${isActive ? 'active' : ''}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.key}
                  to={item.to}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setOpenGroups([])}
                >
                  <span className="sidebar-item-icon">{item.icon}</span>
                  <span className="sidebar-item-label">{item.label}</span>
                </NavLink>
              )
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}

Sidebar.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  mobileOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};